import classNames from "classnames";
import React, { useState, useCallback, useEffect, useContext } from "react";
import { useDropzone } from "react-dropzone";
import { Plus, Download, X, FileText, Trash } from "lucide-react";
import { SnackBarContext } from "components/snack-bar";
import { useStore } from "store";
import { StoreType } from "store/types";
import { getFlyerSizeDisplay, displayInInch } from "pages/flyer/builder/utils";
import { PossibleFlyerType, TemplateConfig } from "pages/flyer/builder/drawer/types";
import { useDragAndDropDesign } from "pages/flyer/upload/hooks/useDragAndDropDesign";
import { getEffectiveFormat, isCatalogueOnlyFormat, isRuntimeFormat } from "../../flyer/builder/formats/formatsStore";
import { useCampaignFormats } from "../../flyer/builder/formats/useFormats";
import { toFormatChannel } from "../../flyer/builder/formats/channels";
import { getArtworkTemplateUrl } from "../../flyer/builder/formats/templates";
import { TemplatePendingNote } from "../../flyer/builder/formats/TemplatePendingNote";
import { DMDocumentFormats, ADMDocumentFormats } from "../../../shared/constants";
import Button from "./Button";
import FormatSelector from "./FormatSelector";

// PDF Preview Component
interface PDFPreviewProps {
  file: File;
  onRemove: () => void;
  pages?: Array<{
    displayWidth: number;
    displayHeight: number;
    pageNumber: number;
    dataUrl: string;
  }>;
}

interface UploadAreaProps {
  onFileUpload?: (file: any) => void;
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[];
  isUploading?: boolean;
  uploadProgress?: number;
  flyerInfo?: { getFlyerInfo: () => Promise<void> };
  genericDesignUpload?: boolean;
  localMode?: boolean;
  onFormatChange?: (format: string) => void;
  onRemoveFile?: () => void;
  clearAndTriggerNew?: boolean;
  _selectedFlyerType?: string;
}

const UploadArea: React.FC<UploadAreaProps> = ({
  maxFileSize = 50,
  acceptedFileTypes = ["application/pdf"],
  isUploading: externalIsUploading,
  uploadProgress: externalUploadProgress,
  onFileUpload,
  genericDesignUpload = false,
  localMode = false,
  onFormatChange,
  onRemoveFile,
  clearAndTriggerNew = false,
  _selectedFlyerType,
}) => {
  const runSnackBar = useContext(SnackBarContext);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Re-request on mount: template URLs expire, and the registry must match this campaign's country and channel
  useCampaignFormats({ refreshOnMount: true });
  const {
    country,
    measurementUnit,
    campaign: { channel, subtype },
  }: StoreType = useStore();
  const fullFormat = getEffectiveFormat(_selectedFlyerType);
  const templateName = fullFormat?.name;
  const templateUrl = fullFormat ? getArtworkTemplateUrl(fullFormat, toFormatChannel(channel, subtype)) : undefined;

  // Use the existing useDragAndDrop hook
  const { isUploading, fileError, pdfPages, onDrop, onDropRejected } = useDragAndDropDesign({
    files,
    setFiles,
    onFileUpload: (design) => {
      // Call onFileUpload only after upload is complete (when design object is returned)
      onFileUpload?.(design);
    },
    format: _selectedFlyerType as PossibleFlyerType,
  });

  // const isUploadingState = externalIsUploading !== undefined ? externalIsUploading : isUploading;
  // const uploadProgressState = externalUploadProgress !== undefined ? externalUploadProgress : fileUploadProgress;

  // Enhanced onDrop with additional validation
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(null);

      // Check file size
      const oversizedFiles = acceptedFiles.filter((file) => file.size > maxFileSize * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        const errorMsg = `Error: File size exceeds ${maxFileSize}MB limit`;
        setError(errorMsg);
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: errorMsg,
        });
        return;
      }

      if (localMode) {
        onFileUpload?.(acceptedFiles[0]);
        runSnackBar({
          type: "success",
          vertical: "top",
          msg: "File uploaded successfully",
        });
      } else {
        // Just call onDrop - onFileUpload will be called after upload completes
        onDrop(acceptedFiles);
      }
    },
    [maxFileSize, onDrop, onFileUpload, localMode, runSnackBar]
  );

  // Enhanced onDropRejected with additional error handling
  const handleDropRejected = useCallback(
    (fileRejections: any[]) => {
      const errors = fileRejections
        .map((rejection) =>
          rejection.errors.map((error: any) => {
            if (error.code === "file-invalid-type") {
              return "Error: You can upload only PDF files";
            }
            if (error.code === "file-too-large") {
              return `Error: File size exceeds ${maxFileSize}MB limit`;
            }
            return error.message;
          })
        )
        .flat();

      const errorMsg = errors[0] || "Error: Invalid file";
      setError(errorMsg);
      onDropRejected(fileRejections);
    },
    [maxFileSize, onDropRejected]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    maxFiles: 1,
    accept: ["application/pdf", "pdf"],
    multiple: false,
  });

  const removeFile = useCallback(() => {
    setFiles([]);
    setError(null);
    onRemoveFile?.();
  }, [onRemoveFile]);

  const handleDownloadTemplate = useCallback(() => {
    if (templateUrl) {
      window.open(templateUrl, "_blank", "noreferrer");
    }
  }, [templateUrl]);

  // Update error state when fileError changes with debouncing to prevent flashing
  React.useEffect(() => {
    if (fileError) {
      const timer = setTimeout(() => {
        setError(fileError);
      }, 100); // Small delay to prevent flashing
      return () => clearTimeout(timer);
    } else {
      setError(null); // Clear immediately for better UX
    }
  }, [fileError]);

  // Handle clear and trigger new upload from parent
  React.useEffect(() => {
    if (clearAndTriggerNew) {
      // Clear current files
      setFiles([]);
      setError(null);
      // Trigger file picker
      setTimeout(() => {
        open();
      }, 100);
    }
  }, [clearAndTriggerNew, open]);

  const getDisplaySize = (format: TemplateConfig) => {
    // Check if format is USPS compliant (ADM/EDDM)
    const isUspsCompliant =
      country.code === "US" &&
      format.format &&
      [...Object.values(DMDocumentFormats), ...Object.values(ADMDocumentFormats)].includes(format.format);

    // For USPS compliant formats (ADM/EDDM), display as Height x Width (USPS standard)
    // For other formats, display as Width x Height (standard)
    if (isUspsCompliant) {
      return getFlyerSizeDisplay(format.heightInMm, format.widthInMm, displayInInch(country.code, measurementUnit));
    }

    return getFlyerSizeDisplay(format.widthInMm, format.heightInMm, displayInInch(country.code, measurementUnit));
  };

  const isFileUploaded = files.length > 0;

  return (
    <div
      className={classNames("space-y-6 transition-all duration-300 ease-in-out", {
        "pointer-events-none cursor-progress opacity-70": isUploading,
      })}
    >
      <div className="flex gap-6">
        <div className="flex-1">
          <label className="text-blue-deep-dark dark:text-foreground text-base font-normal mb-2 block">
            Design file:
          </label>
          <div
            className={classNames(
              "border border-light-blue dark:border-border rounded-lg p-4 bg-white dark:bg-card transition-all duration-300 ease-in-out hover:shadow-md will-change-[box-shadow,border-color] min-h-[120px] flex items-center",
              {
                "border-red-500 bg-red-50 dark:bg-destructive/15": error,
                "bg-[#F2F2F6] dark:bg-accent border-green-400": isFileUploaded && !error,
              }
            )}
          >
            {isFileUploaded && !isUploading ? (
              <PDFPreview file={files[0]} onRemove={removeFile} pages={pdfPages} />
            ) : (
              <div
                {...getRootProps()}
                className={classNames(
                  "border-2 border-dashed rounded-lg py-8 text-center cursor-pointer transition-all duration-200 ease-in-out hover:shadow-lg will-change-[box-shadow,border-color,background-color] w-full min-h-[80px] flex items-center justify-center",
                  isDragActive
                    ? "border-blue-600 bg-blue-50 dark:bg-accent shadow-md"
                    : "border-blue-light dark:border-border hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-accent",
                  localMode ? "px-2" : "px-16",
                  isUploading ? "animate-pulse" : ""
                )}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-2">
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-solid border-[#EE4360]"></div>
                  ) : (
                    <Plus className="w-8 h-8 text-blue-deep-dark dark:text-foreground transition-transform duration-200 ease-in-out group-hover:scale-110" />
                  )}
                  <div className="space-y-1" data-cypress-id={`uploadAreaDragAndDropArea`}>
                    <div
                      className="text-blue-deep-dark dark:text-foreground text-sm"
                      data-cypress-id={`uploadAreaDragAndDropText`}
                    >
                      {isDragActive ? "Drop your PDF here" : "Drag and drop your design here"}
                    </div>
                    <div
                      className="text-grey-dark dark:text-muted-foreground text-xs"
                      data-cypress-id={`uploadAreaFileAcceptedText`}
                    >
                      Files accepted PDF only
                    </div>
                    <div
                      className="text-grey-dark dark:text-muted-foreground text-xs"
                      data-cypress-id={`uploadAreaMaxFileSizeText`}
                    >
                      Max file size is {maxFileSize} Mb
                    </div>
                    <div
                      className="text-grey-dark dark:text-muted-foreground text-xs"
                      data-cypress-id={`uploadAreaTwoSidesInOneText`}
                    >
                      Two sides in one PDF
                    </div>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="mt-2 p-2 bg-red-50 dark:bg-destructive/15 border border-red-200 dark:border-destructive rounded text-red-600 dark:text-destructive text-sm transition-all duration-200 hover:shadow-md">
                {error}
              </div>
            )}
          </div>
        </div>
        {localMode ? null : (
          <div>
            <label className="text-blue-deep-dark dark:text-foreground text-base font-normal mb-2 block">
              Selected format:
            </label>
            <FormatSelector
              value={_selectedFlyerType}
              onFormatChange={onFormatChange}
              selectMode={genericDesignUpload}
            />
          </div>
        )}
      </div>
      {localMode ? null : (
        <div className="space-y-4">
          <div
            className="text-blue-deep-dark dark:text-foreground text-base"
            data-cypress-id={`uploadAreaYourUploadedFileMustText`}
          >
            Your uploaded file must:
          </div>
          <div className="space-y-3 pl-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-secondary-deep-blue dark:bg-foreground rounded-full" />
              <span
                className="text-blue-deep-dark dark:text-foreground text-base"
                data-cypress-id={`uploadAreaBeInSmykText`}
              >
                Be in CMYK color profile
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-secondary-deep-blue dark:bg-foreground rounded-full" />
              <span
                className="text-blue-deep-dark dark:text-foreground text-base"
                data-cypress-id={`uploadAreaContainBothSidesText`}
              >
                Contain both front & back sides of the flyer
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-secondary-deep-blue dark:bg-foreground rounded-full" />
              <span
                className="text-blue-deep-dark dark:text-foreground text-base"
                data-cypress-id={`uploadAreaContainBleedLineText`}
              >
                Contain bleed line, indicating where the edges are cut.
              </span>
            </div>
          </div>
        </div>
      )}

      {!templateUrl && isCatalogueOnlyFormat(fullFormat) && (
        <TemplatePendingNote cypressId="uploadAreaTemplatePending" />
      )}

      {templateUrl && (
        <div
          className="flex w-fit items-center gap-2 cursor-pointer text-muted-foreground transition-colors duration-200 ease-in-out hover:text-foreground"
          onClick={handleDownloadTemplate}
        >
          <Download className="w-5 h-5 transition-transform duration-200 ease-in-out" />
          <Button
            variant="link"
            className="!text-current text-sm p-0 h-auto no-underline hover:underline font-normal transition-colors duration-200 ease-in-out"
            onClick={handleDownloadTemplate}
            data-cypress-id={`uploadAreaDownloadTemplateLink`}
          >
            Download {isRuntimeFormat(fullFormat) ? templateName : `${templateName} (${getDisplaySize(fullFormat!)})`}{" "}
            template
          </Button>
        </div>
      )}
    </div>
  );
};

const PDFPreview: React.FC<PDFPreviewProps> = ({ file, onRemove, pages }) => {
  if (!pages || pages.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pages.length > 0 && (
        <div className="flex flex-row items-center justify-center gap-4">
          {pages.map((page, index) => (
            <div key={index} className="flex flex-col items-center">
              <div className="text-xs text-gray-500">{page.pageNumber === 1 ? "Front" : "Back"}</div>
              <div className="border border-gray-200 dark:border-border rounded-lg overflow-hidden transition-all duration-200 ease-in-out hover:shadow-md">
                <img
                  src={page.dataUrl}
                  alt={`Page ${page.pageNumber}`}
                  style={{
                    width: page.displayWidth,
                    height: page.displayHeight,
                    objectFit: "contain",
                  }}
                  className="block"
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-deep-dark dark:text-foreground" />
          <span className="text-blue-deep-dark dark:text-foreground text-sm">{file.name}</span>
        </div>
        <button
          onClick={onRemove}
          className="p-1 hover:bg-gray-100 dark:hover:bg-accent border-left border-light-blue dark:border-border border-r-0 border-t-0 border-b-0 transition-all duration-200 ease-in-out hover:scale-110 transform rounded"
        >
          <Trash className="w-4 h-4 text-gray-500 hover:text-red-500 transition-colors duration-200 ease-in-out" />
        </button>
      </div>
    </div>
  );
};

export default UploadArea;
